/**
 * Shared vocabulary for Palimpsest.
 *
 * This file is the "contract" between subsystems. The player controller writes
 * telemetry, the analyzer turns telemetry into a PreferenceVector, the evolution
 * engine reads that vector, and the narrative system watches the whole thing —
 * all of them speak the types defined here and nothing else. Keeping the contract
 * in one place is what lets the systems stay decoupled and independently testable.
 */

import type { Vector3 } from 'three';

/* ------------------------------------------------------------------ */
/* Player preference model                                            */
/* ------------------------------------------------------------------ */

/**
 * The seven axes the city infers about the player. Every value is a normalized
 * affinity in [0, 1]. The city never observes the player's *intent* — only these
 * inferred affinities, which is the whole conceit: it is guessing.
 */
export interface PreferenceVector {
  /** Affinity for height, rooftops, vertical traversal. */
  verticality: number;
  /** Affinity for jumping, gaps, dynamic athletic movement. */
  parkour: number;
  /** Preference for narrow, enclosed alleys over open avenues. */
  intimacy: number;
  /** Drive to reach new places vs. dwelling in the familiar. */
  exploration: number;
  /** Preference for moving fast through space. */
  velocity: number;
  /** Tendency to stop and look — to regard vistas. */
  contemplation: number;
  /** Tendency toward transgression (entering forbidden space, aggression). */
  transgression: number;
}

export const PREFERENCE_KEYS = [
  'verticality',
  'parkour',
  'intimacy',
  'exploration',
  'velocity',
  'contemplation',
  'transgression',
] as const;

export type PreferenceKey = (typeof PREFERENCE_KEYS)[number];

export function emptyPreferences(fill = 0): PreferenceVector {
  return {
    verticality: fill,
    parkour: fill,
    intimacy: fill,
    exploration: fill,
    velocity: fill,
    contemplation: fill,
    transgression: fill,
  };
}

/** Return the strongest preference axis and its value. */
export function dominantPreference(p: PreferenceVector): { key: PreferenceKey; value: number } {
  let key: PreferenceKey = 'exploration';
  let value = -Infinity;
  for (const k of PREFERENCE_KEYS) {
    if (p[k] > value) {
      value = p[k];
      key = k;
    }
  }
  return { key, value };
}

/* ------------------------------------------------------------------ */
/* Per-frame player telemetry                                         */
/* ------------------------------------------------------------------ */

/**
 * A lightweight snapshot the player controller emits every frame. The analyzer
 * consumes these; nothing else should. Kept flat and primitive for cheap
 * accumulation.
 */
export interface PlayerTelemetry {
  position: Vector3;
  /** Horizontal speed in world units/second. */
  speed: number;
  /** Height above the ground plane. */
  height: number;
  /** True while the player is not standing on the ground plane (in air or on a roof). */
  elevated: boolean;
  /** True on the frame a jump was initiated. */
  jumped: boolean;
  /** Openness of the immediate surroundings in [0,1]: 0 = tight alley, 1 = plaza. */
  enclosure: number;
  /** Pitch of the view in radians; positive looks up. */
  lookPitch: number;
  /** Whether the player is effectively stationary this frame. */
  still: boolean;
  dt: number;
}

/* ------------------------------------------------------------------ */
/* Application event map                                              */
/* ------------------------------------------------------------------ */

export interface EvolutionEventPayload {
  /** Human-readable description of what the city just decided to do. */
  action: string;
  /** Which preference drove the decision. */
  driver: PreferenceKey;
  /** Overall city maturation progress in [0,1]. */
  maturity: number;
  /** World-space centre of the change, when the action has a locus — used for
   *  the ground ripple and any spatialized feedback. */
  epicenter?: { x: number; z: number };
}

export interface NarrativeBeatPayload {
  id: string;
  text?: string;
  /** 0..1 position through the whole narrative arc. */
  progress: number;
}

/**
 * Phases of the finale ceremony ("The Remembering"):
 *   summon  — the light platform waits at the monolith; the city calls.
 *   ascend  — the player is lifted above the city; their walked path inks in.
 *   recall  — every player-caused structure withdraws; the seed city remains.
 *   rebloom — the built city returns as a radial wave from beneath the player.
 *   descend — the light lowers the player back to the ground.
 *   done    — epilogue: the path stays, faint, forever.
 */
export type FinalePhase = 'summon' | 'ascend' | 'recall' | 'rebloom' | 'descend' | 'done';

export interface FinalePhasePayload {
  phase: FinalePhase;
}

export interface AppEvents extends Record<string, unknown> {
  /** Fired once when the first frame has rendered and the world is interactive. */
  'app:ready': void;
  'app:pointerlock': { locked: boolean };
  /** Player telemetry, every frame. */
  'player:telemetry': PlayerTelemetry;
  /** Emitted when the analyzer publishes an updated preference estimate. */
  'ai:preferences': PreferenceVector;
  /** The city has enacted a growth/decay action. */
  'city:evolved': EvolutionEventPayload;
  /** A narrative beat has been reached. */
  'narrative:beat': NarrativeBeatPayload;
  /** The finale ceremony advanced to a new phase (audio/UI/environment react). */
  'finale:phase': FinalePhasePayload;
}
